import React from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AppTabs from '../../../components/designe/app-tabs/AppTabs';
import AppHelpTooltipButton from '../../../components/designe/tooltips/AppHelpTooltipButton';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import AppNavigate from '../../../components/reusable/app-navigate/AppNavigate';
import ChoosePlanDialog from '../../../components/reusable/choose-plan-dialog/ChoosePlanDialog';
import { useUserConfig } from '../../../context/UserConfigContext';
import { usePullRefreshInvalidateTags } from '../../../hooks/usePullRefreshInvalidateTags';
import { exercisesApi } from '../../../redux/api/exercisesAPI';
import { EXERCISE_PAGES } from '../../../redux/constants';
import { Paths } from '../../../shared/paths';
import ExerciseTooltipContent from './ExerciseTooltipContent';
import { useStyles } from './Exercises.styles';
import FavoriteExercises from './favorite-exercises/FavoriteExercises';
import MyExercises from './my-exercises/MyExercises';
import PublicExercises from './public-exercises/PublicExercises';
import PublishedByUsersExercises from './published-by-users-exercises/PublishedByUsersExercises';
import { useExercisesPage } from './useExercisesPage';

const Exercises = () => {
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const { options } = useUserConfig();
  const {
    tabsConfig,
    planDialogOpened,
    setPlanDialogOpen,
    isSharingExercisesActive,
  } = useExercisesPage();
  const { onRefresh } = usePullRefreshInvalidateTags(
    [
      {
        apiSlice: exercisesApi,
        tags: [
          EXERCISE_PAGES.CREATED_BY_USER,
          EXERCISE_PAGES.FAVORITE,
          EXERCISE_PAGES.PUBLIC,
          EXERCISE_PAGES.ALL_FOR_ADDING,
        ],
      },
    ],
    {
      hideDurationMs: 0,
    },
  );
  const showTooltips = options.showTooltips;
  const pageHeaderTooltip = showTooltips ? (
    <Box className={classes.headerTooltip}>
      <AppHelpTooltipButton
        placement="bottom-end"
        scrollableContent
        content={<ExerciseTooltipContent t={t} />}
      />
    </Box>
  ) : null;

  return (
    <PageContainer
      title={t('common.exercises')}
      description={t('exercises.exercisesDescription')}
      headerContent={pageHeaderTooltip}
      onRefresh={onRefresh}>
      <div className={classes.exercisesWrapper}>
        <AppTabs config={tabsConfig} />

        <div className={classes.tabsContent}>
          <Routes>
            <Route
              path={Paths.exercises.public}
              element={<PublicExercises />}
            />
            <Route
              path={Paths.exercises.my}
              element={<MyExercises setPlanDialogOpen={setPlanDialogOpen} />}
            />
            {isSharingExercisesActive && (
              <Route
                path={Paths.exercises.favorite}
                element={<FavoriteExercises />}
              />
            )}
            {isSharingExercisesActive && (
              <Route
                path={Paths.exercises.publishedByUsers}
                element={<PublishedByUsersExercises />}
              />
            )}
            <Route
              path="*"
              element={<AppNavigate to={Paths.exercises.toPublic} replace />}
            />
          </Routes>
        </div>
      </div>
      <ChoosePlanDialog
        open={planDialogOpened}
        onClose={() => setPlanDialogOpen(false)}
        variant="subscriptionLimit"
      />
    </PageContainer>
  );
};

export default Exercises;
